import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PageHeader } from "@/components/common/PageHeader";
import { knowledgeService } from "@/services";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";
import {
  BookOpen, FileText, HelpCircle, FolderArchive, ShieldCheck, Clock, Eye, Boxes, Loader2, Search,
} from "lucide-react";

const COLORS = ["#10b981", "#f59e0b", "#0ea5e9", "#8b5cf6", "#ef4444", "#64748b"];

function Kpi({ label, value, icon: Icon, color }) {
  return (
    <div className="bg-card rounded-xl border border-border p-5 shadow-sm" data-testid={`kb-kpi-${label}`}>
      <div className="flex items-center justify-between">
        <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{label}</p>
        <Icon className={`h-5 w-5 ${color}`} />
      </div>
      <p className="text-2xl font-bold text-foreground mt-3">{value ?? "—"}</p>
    </div>
  );
}

function ChartCard({ title, data, testid }) {
  const filtered = (data || []).filter((d) => d.value > 0);
  return (
    <div className="bg-card rounded-xl border border-border p-5 shadow-sm" data-testid={testid}>
      <h3 className="font-semibold text-foreground mb-4">{title}</h3>
      {filtered.length === 0 ? <p className="text-sm text-muted-foreground py-10 text-center">No data</p> : (
        <ResponsiveContainer width="100%" height={220}>
          <PieChart>
            <Pie data={filtered} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label>
              {filtered.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
            </Pie>
            <Tooltip />
          </PieChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}

export default function KnowledgeDashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { knowledgeService.dashboard().then(setData).catch(() => {}).finally(() => setLoading(false)); }, []);

  if (loading) return <div className="flex justify-center py-20 text-muted-foreground"><Loader2 className="h-6 w-6 animate-spin" /></div>;

  return (
    <div data-testid="knowledge-dashboard-page">
      <PageHeader title="Knowledge Dashboard" subtitle="Governed knowledge base — articles, FAQs and policy documents"
        actions={
          <button onClick={() => navigate("/admin/knowledge/search")} className="inline-flex items-center gap-2 bg-primary text-primary-foreground rounded-lg px-4 py-2 text-sm font-semibold hover:bg-primary/90" data-testid="kb-go-search">
            <Search className="h-4 w-4" /> Search Knowledge
          </button>
        }
      />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
        <Kpi label="Articles" value={data?.articles} icon={FileText} color="text-sky-500" />
        <Kpi label="FAQs" value={data?.faqs} icon={HelpCircle} color="text-emerald-500" />
        <Kpi label="Documents" value={data?.documents} icon={FolderArchive} color="text-violet-500" />
        <Kpi label="Published" value={data?.published} icon={ShieldCheck} color="text-green-500" />
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-7">
        <Kpi label="Pending Approval" value={data?.pendingApproval} icon={Clock} color="text-amber-500" />
        <Kpi label="Categories" value={data?.categories} icon={BookOpen} color="text-blue-500" />
        <Kpi label="RAG Chunks" value={data?.ragChunks} icon={Boxes} color="text-fuchsia-500" />
        <Kpi label="Top View" value={data?.topViewed?.[0]?.viewCount ?? 0} icon={Eye} color="text-teal-500" />
      </div>
      <div className="grid lg:grid-cols-2 gap-6">
        <ChartCard title="Articles by Status" data={data?.byStatus} testid="kb-by-status" />
        <ChartCard title="Articles by Visibility" data={data?.byVisibility} testid="kb-by-visibility" />
      </div>
      <div className="bg-card rounded-xl border border-border p-5 shadow-sm mt-6" data-testid="kb-top-viewed">
        <h3 className="font-semibold text-foreground mb-4">Most Viewed Articles</h3>
        {(!data?.topViewed || data.topViewed.length === 0) ? (
          <p className="text-sm text-muted-foreground py-6 text-center">No published articles yet</p>
        ) : (
          <div className="space-y-1.5">
            {data.topViewed.map((a) => (
              <button key={a.id} onClick={() => navigate(`/admin/knowledge/articles/${a.id}`)}
                className="flex w-full items-center justify-between text-sm border-b border-border py-2 hover:bg-muted/40 px-2 rounded-md" data-testid={`kb-top-${a.id}`}>
                <span className="text-foreground font-medium text-left">{a.title}</span>
                <span className="text-xs text-muted-foreground flex items-center gap-1"><Eye className="h-3.5 w-3.5" /> {a.viewCount ?? 0}</span>
              </button>
            ))}
          </div>
        )}
      </div>
      <div className="mt-6 rounded-xl border border-dashed border-border p-4 text-xs text-muted-foreground flex items-center gap-2" data-testid="kb-rag-note">
        <Boxes className="h-4 w-4 text-fuchsia-500" />
        Published knowledge is automatically split into <strong className="text-foreground">{data?.ragChunks ?? 0} RAG-ready chunks</strong>. Embeddings are a placeholder — vector search activates in a future AI phase.
      </div>
    </div>
  );
}
